import { getDefaultValues, type RepresentanteForm, type SujetoForm } from '@/components/Interviniente/config';

import { ID_PAIS, TIPO_DIRECCION, TIPO_PERSONA } from '@/constants/tramite.constants';
import { type FormValues, type IRepresentante, type ISujetoListado } from '@/interfaces/model/don.interface';
import { type ILookup } from '@/interfaces/model/generic.interface';
import {
	type TipoIntervencion,
	type IMaestro,
	type IMaestrosGeneric,
	type ImaestrosTramite,
} from '@/interfaces/model/maestros.interface';
import { findID, getID } from '@/utils/id';

import { getLookup } from './dropdown';
import { cleanObj } from './objects';

/** Obtener los valores globales de formulario con el nuevo sujeto. */
export const updateGlobalWithSujeto = (
	currentSujeto: ISujetoListado,
	globalValues: FormValues,
	tipoIntervencionMaestro: TipoIntervencion[],
): FormValues => {
	const { sujetosAdicionales, sujetosRequeridos } = globalValues;
	const { tipoIntervencion, id, idReact } = currentSujeto;

	const minimoObligatorio = tipoIntervencionMaestro.find((t) => t.id === tipoIntervencion.id)?.minimoObligatorio ?? 0;

	const isRequerido = minimoObligatorio > 0;

	// 2. Clonamos la estructura base para mantener inmutabilidad
	const newGlobalValues: FormValues = {
		...globalValues,
		sujetosAdicionales: [...sujetosAdicionales],
		sujetosRequeridos: [...sujetosRequeridos],
	};

	if (isRequerido) {
		newGlobalValues.sujetosRequeridos = newGlobalValues.sujetosRequeridos.map((group) => {
			if (group.tipoIntervencion.id !== tipoIntervencion.id) return group;

			const newItems = [...group.items];
			const index = findID({ list: newItems, id, idReact });

			if (index === -1) newItems.push(currentSujeto);
			else newItems[index] = currentSujeto;

			return { ...group, items: newItems };
		});
	} else {
		const index = findID({
			list: newGlobalValues.sujetosAdicionales,
			id,
			idReact,
		});

		if (index === -1) {
			newGlobalValues.sujetosAdicionales.push(currentSujeto);
		} else {
			newGlobalValues.sujetosAdicionales[index] = currentSujeto;
		}
	}

	return newGlobalValues;
};

export const mapRepresentanteFormToDB = (
	data: RepresentanteForm,
	maestros: Record<'tipoDocumentoIdent' | 'tipoDireccion', IMaestro[]>,
) => {
	const { idTipoDocumentoIdent, id, idReact, direccion, ...rest } = data;
	const { idTipoDireccion, ...restDireccion } = direccion;
	const sinDireccion = idTipoDireccion === '0';

	const dataMapped: IRepresentante = {
		...cleanObj(rest),
		...getID(id, idReact),
		tipoDocumentoIdent: getLookup(idTipoDocumentoIdent, maestros.tipoDocumentoIdent) as ILookup,
		direccion: sinDireccion
			? undefined
			: {
				...cleanObj(restDireccion),
				tipoDireccion: getLookup(idTipoDireccion, maestros.tipoDireccion) as ILookup,
			},
	};

	return dataMapped;
};

export const mapSujetoFormToDB = (
	data: SujetoForm,
	maestros: Pick<ImaestrosTramite, 'tipoDocumentoIdent' | 'tipoIntervencion' | 'tipoPersona'> &
	Pick<IMaestrosGeneric, 'tipoDireccion'>,
) => {
	const { idTipoDocumentoIdent, idTipoPersona, idTipoIntervencion, id, idReact, representantes, direccion, ...rest } =
		data;
	const { idTipoDireccion, ...restDireccion } = direccion;
	const sinDireccion = idTipoDireccion === '0';

	const dataMapped: ISujetoListado = {
		...cleanObj(rest),
		...getID(id, idReact),
		representantes: idTipoPersona === String(TIPO_PERSONA.JURIDICA) ? representantes : undefined,
		tipoDocumentoIdent: getLookup(idTipoDocumentoIdent, maestros.tipoDocumentoIdent) as ILookup,
		tipoPersona: getLookup(idTipoPersona, maestros.tipoPersona) as ILookup,
		tipoIntervencion: getLookup(idTipoIntervencion, maestros.tipoIntervencion) as ILookup,
		direccion: sinDireccion
			? undefined
			: {
				...cleanObj(restDireccion),
				tipoDireccion: getLookup(idTipoDireccion, maestros.tipoDireccion) as ILookup,
			},
	};

	return dataMapped;
};

export const mapSujetoDBToForm = (data: ISujetoListado) => {
	const { tipoDocumentoIdent, tipoPersona, tipoIntervencion, direccion, id, idReact, telefonoCodigoPais, ...rest } =
		data;
	const { tipoDireccion, ...restDireccion } = direccion ?? {};
	const idTipoDireccion = tipoDireccion?.id ? tipoDireccion?.id : TIPO_DIRECCION.SIN_DIRECCION;

	/**
	 * Mapea los datos del formulario asegurando la integridad de los campos para la validación.
	 * Se aplican `defaultValues` como base para evitar que los campos vacíos (falsy) desaparezcan
	 * tras el filtrado del backend. Esto permite que React Hook Form detecte correctamente
	 * el estado `isDirty` cuando un usuario limpia un campo (string vacío).
	 */
	const dataMapped: SujetoForm = {
		...getDefaultValues(),
		...rest,
		...getID(id, idReact),
		telefonoCodigoPais: tipoPersona.id === TIPO_PERSONA.FISICA ? (telefonoCodigoPais ?? ID_PAIS.ESPANA) : '',
		idTipoDocumentoIdent: String(tipoDocumentoIdent.id),
		idTipoPersona: String(tipoPersona.id),
		idTipoIntervencion: String(tipoIntervencion.id),
		direccion: {
			...getDefaultValues().direccion,
			...restDireccion,
			idTipoDireccion: String(idTipoDireccion),
		},
	};

	return dataMapped;
};

export const mapRepresentanteDBToForm = (data: IRepresentante) => {
	const { tipoDocumentoIdent, direccion, id, idReact, ...rest } = data;
	const { tipoDireccion, ...restDireccion } = direccion ?? {};
	const idTipoDireccion = tipoDireccion?.id ? tipoDireccion?.id : TIPO_DIRECCION.SIN_DIRECCION;

	const dataMapped: RepresentanteForm = {
		...rest,
		...getID(id, idReact),
		idTipoDocumentoIdent: String(tipoDocumentoIdent.id),
		direccion: {
			...restDireccion,
			idTipoDireccion: String(idTipoDireccion),
		},
	};

	return dataMapped;
};
